/**
 * tutorScoreAdapter — la tranche `state.data.tutorMonthlyScore` → « Mon score » (`UITutorScore`) et la
 * boîte « Nouveau mois, nouveau départ » (`UITutorMonthReset`).
 *
 * Port littéral de l'ancienne carte `TutorScoreCardM.js` (montée par `TutorHomeTabScreen.js` avec
 * `isMonthly`, sans `isAdmin`) et de `TutorRankingResetModal.js`. La tranche est remplie par le SUCCESS
 * de `tutorMonthlyScoreService.getAllScores` (`GET /api/v1/tutor/scores/all`,
 * `TutorMonthlyScoreController::getAllScores`) dans le réducteur `tutorMonthlyScore.js`.
 *
 * Ce que le serveur sert, et que ce module encaisse :
 *  - les taux et les moyennes sont des `decimal` Eloquent, donc des CHAÎNES en JSON (« 0.8700 »,
 *    « 4.60 ») ; l'ancienne carte les multipliait, ce qui les convertissait sans le dire ;
 *  - `currentMonth` et `previousMonth` sont TOUJOURS là, remplis de `null` (et `month: null`) quand
 *    aucun score n'existe pour le mois (`getEmptyMonthlyScoreData`) ; `global` est `null` sans score
 *    cumulé ;
 *  - le score cumulé n'a pas de `lives_count` : ses lives sont dans `seniority_smoothing`.
 *
 * Les libellés sortent prêts (sous-lignes des axes, dates) ; seuls les taux et les comptes restent
 * des nombres, que `tutorScoreFormatter` écrit dans la carte.
 */
import moment from "moment"

import { formatDateRange } from "../../../ui/formatters/dateFormatter"
import { percentLabel } from "../../../ui/formatters/tutorScoreFormatter"
import {
    type UITutorMonthReset,
    type UITutorScore,
    type UITutorScoreAxis,
    type UITutorScorePeriod,
} from "../../../ui/types/tutorCoquilleAccueil"

// ── Formes lâches : la tranche vient d'un réducteur en JS, tout est optionnel ──────────

/** Un `decimal` Eloquent arrive en chaîne ; un entier ou un flottant calculé, en nombre. */
type ApiNumber = number | string | null

type ApiSeniority = {
    lives_count?: number | null
    unique_students?: number | null
    lives_count_monthly?: number | null
    unique_students_monthly?: number | null
}

export type ApiTutorScore = {
    month?: string | null
    performance_score?: ApiNumber
    satisfaction_normalized?: ApiNumber
    interaction_normalized?: ApiNumber
    productivity_normalized?: ApiNumber
    punctuality_normalized?: ApiNumber
    average_satisfaction?: ApiNumber
    average_interaction?: ApiNumber
    average_productivity?: ApiNumber
    satisfaction_details?: { response_rate?: ApiNumber } | null
    punctuality_details?: {
        total_sessions?: number | null
        on_time_sessions?: number | null
        absence_sessions?: number | null
    } | null
    seniority_smoothing?: ApiSeniority | null
    lives_count?: number | null
    rank?: number | null
    created_at?: string | null
}

export type ApiTrialConversionStats = {
    completed_trials_count?: number | null
    converted_trials_count?: number | null
    conversion_rate?: ApiNumber
}

/** `state.data.tutorMonthlyScore` (`data/redux/reducers/data/tutorMonthlyScore.js`). */
export type ApiTutorMonthlyScoreState = {
    myMonthlyScore?: ApiTutorScore | null
    myPreviousMonthScore?: ApiTutorScore | null
    myGlobalScore?: ApiTutorScore | null
    bonusInfo?: { has_bonus?: boolean; is_active?: boolean } | null
    trialConversionStats?: ApiTrialConversionStats | null
    previousMonthTrialConversionStats?: ApiTrialConversionStats | null
    globalTrialConversionStats?: ApiTrialConversionStats | null
    shouldShowResetModal?: boolean
    previousMonthScore?: ApiTutorScore | null
}

/** Le seuil de l'ancienne carte : `state.data.staticData.monthly_activity_threshold || 5`. */
const DEFAULT_ACTIVITY_THRESHOLD = 5

const toNumber = (value: ApiNumber | undefined): number | null => {
    if (value == null || value === "") return null
    const n = typeof value === "number" ? value : Number(value)
    return Number.isFinite(n) ? n : null
}

/** « 4,6 », « 23,4 » : une moyenne servie avec deux décimales, écrite à la française sans zéro final. */
const decimalLabel = (value: number): string =>
    new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(value)

/**
 * Les sous-lignes des quatre axes, mots de l'ancienne carte (« Moy: 4.60 » → « Moy. 4,6 »).
 *
 * Divergence voulue : l'ancienne carte écrivait « Moy: - » quand la mesure manquait ; une sous-ligne
 * sans chiffre n'est plus écrite (le nouveau masteur n'a que ses quatre « - »).
 */
function toAxes(score: ApiTutorScore): UITutorScoreAxis[] {
    const averageSatisfaction = toNumber(score.average_satisfaction)
    const responseRate = toNumber(score.satisfaction_details?.response_rate)
    const averageInteraction = toNumber(score.average_interaction)
    const averageProductivity = toNumber(score.average_productivity)
    const punctuality = score.punctuality_details
    const onTime = punctuality?.on_time_sessions
    const total = punctuality?.total_sessions
    const absences = punctuality?.absence_sessions

    const lines = (...candidates: (string | null)[]): string[] =>
        candidates.filter((line): line is string => line != null)

    return [
        {
            key: "satisfaction",
            value: toNumber(score.satisfaction_normalized),
            details: lines(
                averageSatisfaction != null ? `Moy. ${decimalLabel(averageSatisfaction)}` : null,
                responseRate != null ? `Exprimés ${percentLabel(responseRate)}` : null
            ),
        },
        {
            key: "interaction",
            value: toNumber(score.interaction_normalized),
            details: lines(averageInteraction != null ? `Moy. ${decimalLabel(averageInteraction)}/30` : null),
        },
        {
            key: "productivity",
            value: toNumber(score.productivity_normalized),
            details: lines(averageProductivity != null ? `Moy. ${decimalLabel(averageProductivity)}/30` : null),
        },
        {
            key: "punctuality",
            value: toNumber(score.punctuality_normalized),
            details: lines(
                onTime != null && total != null ? `À l'heure ${onTime}/${total}` : null,
                absences != null ? `Absences ${absences}` : null
            ),
        },
    ]
}

/** La conversion des essais ; absente quand le masteur n'en fait pas (`accept_trials`) ou n'en a fini aucun. */
function toTrialConversion(stats: ApiTrialConversionStats | null | undefined): UITutorScorePeriod["trialConversion"] {
    const completed = stats?.completed_trials_count ?? 0
    if (stats == null || completed <= 0) return null
    return {
        rate: toNumber(stats.conversion_rate),
        converted: stats.converted_trials_count ?? 0,
        completed,
    }
}

/**
 * Une période mensuelle (ce mois-ci, le mois dernier).
 *
 * `livesCount` est le `lives_count` du score, celui que l'ancienne carte comparait au seuil (classement,
 * progression, alerte d'assiduité). Elle écrivait sous « Lives » `seniority_smoothing.lives_count_monthly` :
 * le même compte (les lives validés du mois, `getLivesCountForMonth`), calculé au même instant ; il
 * sert de repli.
 */
function toMonthlyPeriod(
    score: ApiTutorScore,
    trial: ApiTrialConversionStats | null | undefined,
    rangeLabel: string,
    withUpdated: boolean
): UITutorScorePeriod {
    const createdAt = score.created_at
    return {
        performance: toNumber(score.performance_score),
        rank: score.rank ?? null,
        livesCount: score.lives_count ?? score.seniority_smoothing?.lives_count_monthly ?? 0,
        studentsCount: score.seniority_smoothing?.unique_students_monthly ?? null,
        axes: toAxes(score),
        trialConversion: toTrialConversion(trial),
        rangeLabel,
        // L'ancienne carte : `new Date(created_at).toLocaleDateString("fr-FR")`, sur ce mois-ci seulement.
        updatedLabel:
            withUpdated && createdAt != null && createdAt !== ""
                ? `Mis à jour le ${moment(createdAt).format("DD/MM/YYYY")}`
                : undefined,
    }
}

/**
 * La période cumulée.
 *
 * Divergence voulue : l'ancienne carte lisait `lives_count` (absent du cumul, donc 0) pour l'alerte
 * d'assiduité, qui ne s'allumait jamais en cumulé, et écrivait sous « Lives »
 * `seniority_smoothing.lives_count`. La forme n'a qu'un compte : c'est celui qu'on lit, et une
 * assiduité cumulée sous la moitié passe désormais en alerte comme celle d'un mois.
 */
function toGlobalPeriod(score: ApiTutorScore, trial: ApiTrialConversionStats | null | undefined): UITutorScorePeriod {
    return {
        performance: toNumber(score.performance_score),
        rank: score.rank ?? null,
        livesCount: score.seniority_smoothing?.lives_count ?? 0,
        studentsCount: score.seniority_smoothing?.unique_students ?? null,
        axes: toAxes(score),
        trialConversion: toTrialConversion(trial),
        rangeLabel: "Score cumulé depuis le début",
    }
}

/** Le gabarit vide du serveur (`getEmptyMonthlyScoreData`) : aucun score n'existe pour ce mois. */
const isEmptyMonth = (score: ApiTutorScore): boolean => score.month == null && toNumber(score.performance_score) == null

/**
 * « Mon score ». `null` tant que la tranche n'a ni ce mois-ci ni le mois dernier — l'ancienne carte
 * rendait `(tutorScore || previousMonthScore) && …`, donc rien avant la première réponse.
 *
 * Les périodes : l'ancienne carte faisait défiler « Ce mois-ci → Le mois dernier → Depuis le début »
 * selon ce que la tranche avait. Divergence voulue : le serveur sert toujours un mois dernier, vide
 * quand le masteur n'avait pas de score — l'ancien bouton menait alors à un mois de « - ». Un mois
 * dernier vide n'est plus offert (le nouveau masteur n'a que « Ce mois-ci »).
 *
 * @param activityThreshold `state.data.staticData.monthly_activity_threshold` ; 5 à défaut, comme l'ancienne.
 */
export function toUITutorScore(
    state: ApiTutorMonthlyScoreState | null | undefined,
    activityThreshold?: number | null
): UITutorScore | null {
    const current = state?.myMonthlyScore
    const previous = state?.myPreviousMonthScore
    if (current == null && previous == null) return null

    const now = moment()
    const lastMonth = moment().add(-1, "month")
    const bonus = state?.bonusInfo

    return {
        activityThreshold: activityThreshold || DEFAULT_ACTIVITY_THRESHOLD,
        // Le Top 3 du mois passé touche son bonus les deux premières semaines (`getTopThreeBonusInfo`).
        bonusActive: bonus?.has_bonus === true && bonus?.is_active === true,
        periods: {
            // Les dates de l'ancienne carte : du 1er du mois à aujourd'hui, puis le mois dernier entier.
            current: toMonthlyPeriod(
                current ?? {},
                state?.trialConversionStats,
                formatDateRange(now.clone().startOf("month").toISOString(), now.toISOString()),
                true
            ),
            ...(previous != null && !isEmptyMonth(previous)
                ? {
                      previous: toMonthlyPeriod(
                          previous,
                          state?.previousMonthTrialConversionStats,
                          formatDateRange(
                              lastMonth.clone().startOf("month").toISOString(),
                              lastMonth.clone().endOf("month").toISOString()
                          ),
                          false
                      ),
                  }
                : {}),
            ...(state?.myGlobalScore != null
                ? { global: toGlobalPeriod(state.myGlobalScore, state.globalTrialConversionStats) }
                : {}),
        },
    }
}

/**
 * « Nouveau mois, nouveau départ » — l'ancienne `TutorRankingResetModal` : ouverte quand
 * `shouldShowResetModal` (le serveur : un score ce mois-ci, un le mois dernier, la boîte pas encore vue
 * ce mois) ET que le score du mois passé est là. `null` : fermée.
 *
 * La fermeture reste à l'écran : `tutorMonthlyScoreService.markResetModalShown` puis `hideResetModal()`.
 */
export function toUITutorMonthReset(state: ApiTutorMonthlyScoreState | null | undefined): UITutorMonthReset | null {
    const score = state?.previousMonthScore
    if (state?.shouldShowResetModal !== true || score == null) return null
    const rank = score.rank ?? null
    return {
        rank,
        performance: toNumber(score.performance_score),
        // L'ancienne : `rank && rank <= 3` — un rang 0 ou absent n'est pas le Top 3.
        topThree: rank != null && rank > 0 && rank <= 3,
    }
}
